import {
  PurchaseOptionType,
  VProductType,
  EcommerceItemType,
  DxpDataLayerEventNames,
  PageType,
  EventDataType,
  DxpPageType,
  DxpDataLayerEvent,
  CarBrands,
  CheckoutStepType,
  FormContextTradeInType,
  TradeInType,
  RetailerType,
  BookingDetailsType,
  EcommerceType,
  OrderType,
} from '../../../../../common/utilities/customDatalayerInterface';
import format from 'date-fns/format';
import { FinanceTypes, AffiliationTypes, EventDataTypes } from '../enum/DataLayerEnums';
import { GetDeal_deal, GetDeal_deal_dealer } from '../graphql/queries/__generated__/GetDeal';
import { IStep } from 'src/gql/checkout4/model/IJourneySteps';
import { FinanceFormType } from '../graphql/__generated__/globalTypes';

export const lowerCaseFields = (
  input: string | Record<string, string | undefined> | undefined,
): Record<string, string> => {
  if (typeof input === 'string') {
    return { value: input.toLowerCase() };
  }

  if (input === undefined) {
    return {};
  }

  const result: Record<string, string> = {};
  for (const key in input) {
    if (Object.prototype.hasOwnProperty.call(input, key)) {
      const val = input[key];
      result[key] = val ? val.toLowerCase() : '';
    }
  }
  return result;
};

export const removeHostNameFromUrl = (fullUrl: string): string => {
  try {
    const parsed = new URL(fullUrl);
    return `${parsed.pathname}${parsed.search}${parsed.hash}`;
  } catch {
    return fullUrl;
  }
};

export const computeMonthly = (value?: number, unit = ''): string | undefined => {
  if (!value) return;
  return `${Math.round(value / 12)}${unit}`;
};

export const formatDate = (date: string): string => {
  return format(new Date(date), 'dd/MM/yyyy');
};

const getCarBrand = (brand: string | undefined): string => {
  const carBrandKey = brand?.replace('-dis', '').toUpperCase();
  const brandLookup: Record<string, string> = Object.fromEntries(
    Object.entries(CarBrands).filter(([key]) => isNaN(Number(key))),
  );

  return brandLookup[carBrandKey as string] || '';
};

export const getPurchaseOptionCommonFields = (deal: GetDeal_deal): Partial<PurchaseOptionType> => {
  const { finance } = deal;

  return {
    payment_type: finance?.type === FinanceFormType.finance ? FinanceTypes.FINANCE : FinanceTypes.CASH,
    monthly_payment: finance?.simulation?.paymentValue[0].amount.toString() ?? '',
    interest_rate: finance?.simulation?.interestRate?.toString() ?? '',
    down_payment: finance?.simulation?.deposit[0].amount.toString() ?? '',
  };
};

export const getAffiliationTypeFromDeal = (deal: GetDeal_deal): AffiliationTypes => {
  const stock = deal?.car?.stock;
  const state = stock?.attributes ? JSON.parse(stock.attributes)?.state : null;

  if (!stock) return AffiliationTypes.NON_STOCK;

  switch (state) {
    case 'new':
      return AffiliationTypes.STOCK_NVL;
    case 'approved':
      return AffiliationTypes.STOCK_AVL;
    default:
      return AffiliationTypes.NON_STOCK;
  }
};

export const getTradeInCommonFields = (deal: GetDeal_deal, tradeIn?: FormContextTradeInType): Partial<TradeInType> => {
  const { makeAutocomplete, model, mileage, year, registrationNumber } = tradeIn || {};

  return lowerCaseFields({
    brand: (deal.attributes?.tradeInMake || makeAutocomplete) ?? '',
    body_type: '',
    fuel_type: '',
    model: (deal.attributes?.tradeInModel || model) ?? '',
    mileage: (deal.attributes?.tradeInMileage || mileage) ?? '',
    date_of_registration: (deal.attributes?.tradeInYear || year) ?? '',
    registration_number: (deal.attributes?.tradeInRegistrationNumber || registrationNumber) ?? '',
  });
};

export const getRetailerCommonFields = (
  deal: GetDeal_deal,
  dealerOverride?: GetDeal_deal_dealer | null,
): Partial<RetailerType> => {
  const dealer = dealerOverride ?? deal.dealer;

  return lowerCaseFields({
    name: dealer?.label ?? '',
    location: dealer?.address?.address ?? '',
    city_name: dealer?.address?.city ?? '',
    zip_code: dealer?.postCode ?? '',
    mobile_number: dealer?.address?.phone ?? '',
    email_address: '',
  });
};

export const getBookingDetailsCommonFields = (
  deal: GetDeal_deal, 
  formData?: { appointment?: { preferredDate?: string; preferredTime?: string } }
): Partial<BookingDetailsType> => {
  const preferredDate = deal?.appointment?.preferredDate || formData?.appointment?.preferredDate;
  let preferredTime = deal?.appointment?.preferredTime || formData?.appointment?.preferredTime;

  if (preferredTime) {
    preferredTime = preferredTime.replace(/^[^\d]+/, '');
    if (/^\d{4}_\d{4}$/.test(preferredTime)) {
      const [start, end] = preferredTime.split('_');
      preferredTime = `${start.slice(0, 2)}:${start.slice(2)}-${end.slice(0, 2)}:${end.slice(2)}`;
    }
    else if (/^\d{2}:\d{2}\.\d{2}:\d{2}$/.test(preferredTime)) {
      preferredTime = preferredTime.replace('.', '-');
    }
  }

  return lowerCaseFields({
    date: preferredDate ? formatDate(preferredDate) : '',
    time: !preferredTime || preferredTime === 'Bezi preferencji' ? '' : preferredTime,
  });
};

export const vproductCommonFields = (deal: GetDeal_deal): Partial<VProductType> => {
  if (!deal || !deal.car) return {};
  const { car, uuid } = deal;
  const { bodyStyle, nameplate, prices, exterior, engine, model } = car;

  if (!car) return {};

  return {
    ...lowerCaseFields({
      series: model?.code,
      name: model?.attributes?.shortname ?? '',
      nameplate: nameplate?.code ?? '',
      bodystyle: bodyStyle?.code ?? '',
      engine: engine?.attributes?.shortname ?? '',
      fuel_type: engine?.attributes?.fuelType ?? '',
      color: exterior?.shortname,
      color_code: exterior?.attributes.oemcode,
    }),
    model_year: Number(model?.attributes?.modelyear) ?? 0,
    id: uuid,
    index: '1',
    price: prices?.totalPrice?.[1]?.money?.amount ?? 0,
  };
};

export const getPageCommonFields = (deal: GetDeal_deal): Partial<PageType> => {
  const { language, currency, brand, attributes } = deal;

  return lowerCaseFields({
    page_type: DxpPageType.REAL,
    language: language ?? '',
    currency_code: currency ?? undefined,
    brand: brand && getCarBrand(brand),
    consent_status: '',
    category: DxpDataLayerEvent.CHECKOUT,
    application: getAffiliationTypeFromDeal(deal),
    market: attributes?.market ?? '',
  });
};

export const getEcommerceItemCommonFields = (deal: GetDeal_deal): Partial<EcommerceItemType> => {
  if (!deal || !deal.car) return {};
  const { car, brand } = deal;
  const { displayName, nameplate, prices, exterior, model } = car;

  if (!car) return {};

  return {
    ...lowerCaseFields({
      item_id: nameplate?.code ?? '',
      item_category: displayName ?? '',
      item_name: nameplate?.attributes?.shortname || model?.attributes?.shortname || '',
      item_brand: brand && getCarBrand(brand),
      item_variant: exterior?.shortname ?? '',
      affiliation: getAffiliationTypeFromDeal(deal),
    }),
    index: '1',
    price: prices?.totalPrice?.[1]?.money?.amount ?? '',
    quantity: '1',
  };
};

export const getEventDataCommonFields = (event: DxpDataLayerEventNames, isClicked = false): Partial<EventDataType> => {
  return {
    ...(event !== DxpDataLayerEventNames.BEGIN_CHECKOUT && {
      event_non_int: `${!isClicked}`,
    }),
    event,
  };
};

export const getCheckoutStepsCommonFields = (steps: IStep[], activeStep: number): Partial<CheckoutStepType> => {
  const step = steps[activeStep];
  const stepNumber = activeStep + 1;

  if (!step || !step.pathname) {
    return {
      step_number: stepNumber.toString(),
      step_name: `step-${stepNumber}`,
    };
  }

  const pathName = step.pathname.startsWith('/') ? step.pathname.slice(1) : step.pathname;

  return {
    step_number: stepNumber.toString(),
    step_name: pathName,
  };
};

export const getEcommerceCommonFields = (deal: GetDeal_deal): Partial<EcommerceType> => {
  const { car } = deal;

  return {
    value: car?.prices?.carPrice?.finalPrice?.[0]?.money?.amount.toString() ?? '',
    ...lowerCaseFields({ currency: deal?.currency ?? '' }),
    items: [getEcommerceItemCommonFields(deal)],
  };
};

export const getOrderCommonFields = (deal: GetDeal_deal): Partial<OrderType> => {
  const { car } = deal;
  const reservationDate = deal.paymentInfo?.[0]?.transactionTime?.trim();
  const inquiryDate = deal.createdAt?.split('+')[0]?.trim() || '';
  return {
    date: reservationDate ? reservationDate : inquiryDate,
    number: deal?.uuid,
    vin_number: car?.stock?.vin?.toLowerCase() ?? '',
    status: deal.status ?? '',
  };
};

export const getTradeInTextEventFields = (event: DxpDataLayerEventNames, click_text: EventDataTypes) => {
  return {
    ...getEventDataCommonFields(event, true),
    event_label: `${EventDataTypes.TEXT_INPUT}::${click_text}`,
    click_text,
  };
};
